import { useQuery } from '@tanstack/react-query';

import { TokenContentApiReponse } from '@/lib/api/tokens/types';
import { ClientNetworkError } from '@/lib/errors/client';

import { routes } from '@/utils/routes';
import { objectToQueryString } from '@/utils/string';

export const NATIVE_USDC_HELP_TEXT =
  'Native USDC is issued by Circle and is redeemable 1:1 for US dollars.';

const fetchCustomContent = async (
  tokenAddress: string,
  chainId: number,
): Promise<TokenContentApiReponse> => {
  const response = await fetch(
    `${routes.api.TOKENS_CONTENT}${objectToQueryString({ tokenAddress, chainId })}`,
  );
  if (!response.ok) throw new ClientNetworkError(response.statusText);
  return (await response.json()) as TokenContentApiReponse;
};

export const customContentQueryKey = (
  tokenAddress: string | undefined,
  chainId: number | undefined,
) => ['token-content', tokenAddress, chainId] as const;

type UseCustomContentOptions = {
  initialData?: TokenContentApiReponse;
};

/**
 * Fetches Custom Content for USDC token pages and others.
 */
export const useCustomContent = (
  tokenAddress: string | undefined,
  chainId: number | undefined,
  opts: UseCustomContentOptions = {},
) =>
  useQuery<TokenContentApiReponse, Error>({
    queryKey: customContentQueryKey(tokenAddress, chainId),
    queryFn: () => {
      if (!tokenAddress || !chainId) {
        throw new Error('tokenAddress and chainId are required');
      }
      return fetchCustomContent(tokenAddress, chainId);
    },
    enabled: Boolean(tokenAddress && chainId),
    refetchOnReconnect: false,
    staleTime: 60_000,
    ...opts,
  });
